import { ToolInfo } from '@platfio/interfaces/platfio';

type ToolCompatibilityMetadata = {
  title?: string;
  category?: string;
  groupKey?: string;
  groupLabel?: string;
  groupDescription?: string;
  groupOrder?: number;
};

interface LegacyToolGroupDefinition {
  key: string;
  label: string;
  description: string;
  order: number;
  category: string;
  toolNames: string[];
}

const LEGACY_TOOL_GROUPS: LegacyToolGroupDefinition[] = [
  { key: 'notes', label: 'Notes', description: 'Create, read, update, and remove agency notes.', order: 10, category: 'notes', toolNames: ['createNote', 'readNotes', 'updateNote', 'deleteNote'] },
  { key: 'files', label: 'Files', description: 'Create, read, update, and remove uploaded files for the current client context.', order: 20, category: 'files', toolNames: ['createFile', 'readFile', 'readFiles', 'updateFile', 'deleteFile'] },
  { key: 'submissions', label: 'Submissions', description: 'Inspect captured form submissions and intake responses.', order: 30, category: 'submissions', toolNames: ['readSubmissions'] },
  { key: 'purchases', label: 'Purchases', description: 'Review purchase and order records for the current business.', order: 40, category: 'purchases', toolNames: ['readPurchases'] },
  { key: 'tasks', label: 'Tasks', description: 'Create and manage implementation tasks.', order: 50, category: 'tasks', toolNames: ['createTask', 'readTasks', 'updateTask', 'deleteTask'] },
  { key: 'businesses', label: 'Businesses', description: 'Read, create, and maintain business records.', order: 60, category: 'businesses', toolNames: ['getBusiness', 'getBusinesses', 'createBusiness', 'setBusiness'] },
  { key: 'businessContacts', label: 'Business Contacts', description: 'Read and manage the primary and additional contacts for a business.', order: 65, category: 'businessContacts', toolNames: ['getBusinessContacts', 'addBusinessContact', 'setBusinessContact', 'deleteBusinessContact'] },
  { key: 'businessAutomations', label: 'Business Automations', description: 'Inspect and manage scheduled business automations.', order: 70, category: 'businessAutomations', toolNames: ['getBusinessAutomations', 'addBusinessAutomation', 'setBusinessAutomation', 'deleteBusinessAutomation'] },
  { key: 'agencies', label: 'Agency', description: 'Read and update agency details and agency team data.', order: 80, category: 'agencies', toolNames: ['getAgency', 'getAgencyTeam', 'setAgency'] },
  { key: 'affiliates', label: 'Partners', description: 'Read, update, and delete partner records.', order: 90, category: 'affiliates', toolNames: ['getAffiliate', 'setAffiliate', 'deleteAffiliate'] },
  { key: 'dashboards', label: 'Dashboards', description: 'Inspect dashboard availability and configuration.', order: 100, category: 'dashboards', toolNames: ['getDashboards'] },
  { key: 'app', label: 'App Data', description: 'Inspect app metadata, Firebase settings, and implementation data types and records.', order: 110, category: 'app', toolNames: ['getApps', 'getFirebase', 'dataGetTypesSummary', 'dataGetTypeDetail', 'dataGet', 'dataSet', 'dataIdGenerate'] },
  { key: 'plan', label: 'Plans', description: 'Create plans and manage plan details, personas, stories, and board state.', order: 120, category: 'plan', toolNames: ['createPlan', 'readPlans', 'setPlanDetails', 'setPlanPersona', 'setPlanStory', 'setPlanDrawflowBoard', 'removePlanPersona', 'removePlanStory', 'removePlanDrawflowBoard'] },
  { key: 'proposal', label: 'Proposals', description: 'Create proposals and manage proposal content.', order: 130, category: 'proposal', toolNames: ['createProposal', 'readProposals', 'setProposalDetails'] },
  { key: 'web', label: 'Web Research', description: 'Search the web, extract page content, and source reference imagery.', order: 140, category: 'web', toolNames: ['searchWeb', 'extractWebContent', 'searchUnsplashImages'] },
  { key: 'preview', label: 'Preview', description: 'Open app, note, plan, proposal, ticket, and emulator previews inline.', order: 150, category: 'preview', toolNames: ['render_preview'] },
  { key: 'compute', label: 'Compute', description: 'Initialize a computer session, run commands, inspect artifacts, and pause the session.', order: 160, category: 'compute', toolNames: ['initialize_computer', 'run_terminal_command', 'read_artifact', 'save_and_pause_computer'] },
  { key: 'tickets', label: 'Tickets', description: 'Read tickets, inspect messages, and send ticket replies.', order: 170, category: 'tickets', toolNames: ['getTickets', 'getTicketMessages', 'sendTicketMessage'] }
];

const LEGACY_TOOL_TITLE_OVERRIDES: Record<string, string> = {
  dataGetTypesSummary: 'List App Data Types',
  dataGetTypeDetail: 'Inspect App Data Type',
  dataGet: 'Read App Data',
  dataSet: 'Write App Data',
  dataIdGenerate: 'Generate App Data ID',
  getFirebase: 'Get Firebase Configuration',
  render_preview: 'Render Preview',
  initialize_computer: 'Initialize Computer',
  read_artifact: 'Read Artifact',
  run_terminal_command: 'Run Terminal Command',
  save_and_pause_computer: 'Pause Computer',
  setPlanDrawflowBoard: 'Set Plan Board',
  removePlanDrawflowBoard: 'Remove Plan Board',
  getAffiliate: 'Get Partner',
  setAffiliate: 'Set Partner',
  deleteAffiliate: 'Delete Partner'
};

const LEGACY_TOOL_METADATA_BY_NAME = buildLegacyToolMetadataByName();

function buildLegacyToolMetadataByName(): Record<string, ToolCompatibilityMetadata> {
  const metadataByName: Record<string, ToolCompatibilityMetadata> = {};

  for (const group of LEGACY_TOOL_GROUPS) {
    for (const toolName of group.toolNames) {
      metadataByName[toolName] = {
        ...metadataByName[toolName],
        category: group.category,
        groupKey: group.key,
        groupLabel: group.label,
        groupDescription: group.description,
        groupOrder: group.order
      };
    }
  }

  for (const [toolName, title] of Object.entries(LEGACY_TOOL_TITLE_OVERRIDES)) {
    metadataByName[toolName] = {
      ...metadataByName[toolName],
      title
    };
  }

  return metadataByName;
}

export function getToolCatalogCompatibilityMetadata(tool: ToolInfo): ToolCompatibilityMetadata {
  return LEGACY_TOOL_METADATA_BY_NAME[tool.name] || {};
}

export function isLegacyToolCatalogResponse(tools: ToolInfo[]): boolean {
  return Array.isArray(tools) && tools.length > 0 && tools.every(tool => !tool.definition?.group?.key);
}

export function getLegacyToolCatalogMessage(backendUrl: string, tools: ToolInfo[]): string {
  const action = backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1')
    ? 'Restart localhost:8080'
    : 'Redeploy this backend';
  const missingKnownTools = ['createFile', 'getBusinessContacts', 'deleteAffiliate']
    .filter(toolName => !tools.some(tool => tool.name === toolName));
  const missingSuffix = missingKnownTools.length > 0
    ? ` Missing newer tools: ${missingKnownTools.join(', ')}.`
    : '';

  return `Legacy tool catalog detected. ${action} to load grouped metadata and the newest tool registrations.${missingSuffix}`;
}